import { useState, useRef, useEffect } from "react";
import { ToolsShell, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Reaction-Time Tester. User presses and holds; at a random delay
// (1.5-4.5 s) the color flips to green. Lift finger as fast as possible.
// Measures pointerdown→pointerup interval starting from the color flip.
//
// Penalty: if the user lifts BEFORE the green flip (false start), that
// trial is logged as a false start, not a time. They're encouraged to
// not anticipate.

const TRIALS = 5;

export default function ReactionTimer() {
  const [phase, setPhase] = useState("idle"); // idle | wait | go | done | falseStart
  const [times, setTimes] = useState([]); // ms per trial
  const [trial, setTrial] = useState(0);
  const [lastTime, setLastTime] = useState(null);
  const timerRef = useRef(null);
  const greenStartRef = useRef(0);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const begin = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPhase("wait");
    setLastTime(null);
    const delay = 1500 + Math.random() * 3000;
    timerRef.current = setTimeout(() => {
      greenStartRef.current = performance.now();
      setPhase("go");
    }, delay);
  };

  const onPointerDown = () => {
    if (phase === "idle" || phase === "done" || phase === "falseStart") {
      begin();
    } else if (phase === "wait") {
      // False start
      if (timerRef.current) clearTimeout(timerRef.current);
      setPhase("falseStart");
    }
  };

  const onPointerUp = () => {
    if (phase === "go") {
      const ms = performance.now() - greenStartRef.current;
      setLastTime(ms);
      const nextTimes = [...times, ms];
      setTimes(nextTimes);
      const nextTrial = trial + 1;
      setTrial(nextTrial);
      if (nextTrial >= TRIALS) {
        setPhase("done");
      } else {
        setPhase("idle");
      }
    }
  };

  const resetAll = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setTimes([]); setTrial(0); setLastTime(null); setPhase("idle");
  };

  const avg = times.length ? times.reduce((s, x) => s + x, 0) / times.length : null;
  const best = times.length ? Math.min(...times) : null;
  const worst = times.length ? Math.max(...times) : null;

  const bg = phase === "go" ? "#2D8C3D" : phase === "wait" ? "#C53030" : phase === "falseStart" ? "#8B3A3A" : "#1C1E2E";
  const headline =
    phase === "idle" ? (trial === 0 ? "Tap and hold to start" : "Tap and hold for next trial") :
    phase === "wait" ? "Wait for green…" :
    phase === "go" ? "GO! Release now" :
    phase === "falseStart" ? "Too soon! Tap to retry" :
    "All trials complete";

  return (
    <ToolsShell title="Reaction Time">
      <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
        Tap and hold the panel below. When it turns green, release as fast as you can. {TRIALS} trials.
      </p>

      <div
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          width: "100%", aspectRatio: "4 / 3",
          background: bg,
          borderRadius: 16,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          color: "#fff",
          userSelect: "none",
          touchAction: "manipulation",
          transition: "background 80ms ease",
          cursor: phase === "idle" || phase === "done" || phase === "falseStart" ? "pointer" : "default",
          fontWeight: 700,
          fontSize: "clamp(18px, 5vw, 28px)",
          textAlign: "center",
          padding: 20,
        }}
      >
        <div>{headline}</div>
        {lastTime !== null && phase !== "go" && phase !== "wait" && (
          <div style={{ fontSize: "clamp(28px, 8vw, 48px)", marginTop: 14, fontVariantNumeric: "tabular-nums" }}>
            {Math.round(lastTime)} ms
          </div>
        )}
        <div style={{ fontSize: 14, marginTop: 16, fontWeight: 500, opacity: 0.8 }}>
          Trial {Math.min(trial + 1, TRIALS)} of {TRIALS}
        </div>
      </div>

      {times.length > 0 && (
        <div style={{ marginTop: 18, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "16px 18px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
            <Mini label="Best" value={`${Math.round(best)} ms`} />
            <Mini label="Average" value={`${Math.round(avg)} ms`} />
            <Mini label="Worst" value={`${Math.round(worst)} ms`} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {times.map((t, i) => (
              <div key={i} style={{ background: "#0F1018", color: "#C8CADF", padding: "5px 9px", borderRadius: 6, fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
                #{i + 1}: {Math.round(t)} ms
              </div>
            ))}
          </div>
          {phase === "done" && (
            <button onClick={resetAll} style={{ width: "100%", marginTop: 14, background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Start over
            </button>
          )}
        </div>
      )}

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
        Average human reaction time to a visual stimulus is ~250 ms. Touchscreens add ~30-80 ms of input lag, so results are biased upward.
      </p>
    </ToolsShell>
  );
}

function Mini({ label, value }) {
  return (
    <div style={{ background: "#0F1018", borderRadius: 8, padding: "10px 6px", textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#7C8099", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
